import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Plane, Car, Heart, Mountain, Briefcase, Users, UserCog, MapPinned,
  ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { BUSINESS, SERVICES_DETAILED, SERVICE_HUBS, IMG } from "@/data/site";
import Seo from "@/components/Seo";

const ICONS = { Plane, Car, Heart, Mountain, Briefcase, Users, UserCog, MapPinned };

const wa = (n, m) =>
  `https://wa.me/91${n}?text=${encodeURIComponent(m || "Hello Chakri Travels, I want to book a car rental service.")}`;

const Services = () => {
  useEffect(() => {
    document.title = "Services — Luxury Car Rental, Airport, Weddings & AP Tours | Chakri Travels";
  }, []);
  return (
    <main>
      <Seo
        title="Services — Airport Taxi, Wedding Cars, Outstation & AP Tours | Chakri Travels"
        description="Airport transfers, luxury car rentals, wedding cars, outstation trips, corporate travel, family tours & Konaseema AP tour packages from Kakinada. Book on WhatsApp +91 90591 14439."
        path="/services"
      />
      {/* Hero */}
      <section className="relative isolate flex min-h-[60svh] items-center overflow-hidden">
        <img src={IMG.hero} alt="Premium services" className="absolute inset-0 -z-10 h-full w-full object-cover" />
        <div className="absolute inset-0 -z-10 bg-[var(--royal-deep)]/40" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[var(--royal-deep)]/90 via-[var(--royal-deep)]/75 to-[var(--royal)]/60" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/65 via-transparent to-black/30" />
        <div className="mx-auto w-full max-w-7xl px-5 pt-32 pb-20 lg:px-8 lg:pt-40">
          <div className="max-w-3xl reveal">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/60 glass-dark px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--gold)]">
              Premium Services
            </div>
            <h1 className="font-display text-3xl font-bold leading-[1.1] text-white sm:text-5xl lg:text-6xl hero-text-shadow">
              Every kind of journey, <span className="text-gold-on-image">elevated</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base font-medium text-white sm:text-lg hero-text-shadow-soft">
              From quick airport runs to multi-day AP tour packages, Chakri Travels delivers a polished, chauffeur-driven experience for every occasion.
            </p>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES_DETAILED.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <div
                key={s.title}
                data-testid={`service-detailed-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-[var(--royal)]/10 bg-white p-6 shadow-[0_10px_30px_-15px_rgba(15,23,80,0.18)] transition-all hover:-translate-y-1 hover:border-[var(--gold)]/50"
              >
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[var(--gold)]/10 blur-2xl transition-opacity group-hover:bg-[var(--gold)]/30" />
                <div className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl gradient-royal text-white ring-1 ring-[var(--gold)]/50">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-[var(--royal-deep)]">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
                  <a
                    href={wa(BUSINESS.primaryWhatsApp, `Hi Chakri Travels, I'd like to enquire about ${s.title}.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--royal)] hover:text-[var(--gold)]"
                  >
                    Enquire now <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Service hubs */}
      <div className="bg-[linear-gradient(180deg,white,#f4f6fb)]">
        <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> Where we operate
            </div>
            <h2 className="font-display text-3xl font-bold text-[var(--royal-deep)] sm:text-5xl">
              Proudly serving the <span className="text-gradient-gold">heart of AP</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Every service is available across our four core service hubs — and we travel anywhere you need to go.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICE_HUBS.map((h) => (
              <div key={h} className="rounded-2xl bg-white p-6 ring-1 ring-[var(--royal)]/10 shadow-[0_10px_30px_-15px_rgba(15,23,80,0.18)] hover:-translate-y-1 transition-transform">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl gradient-gold text-[var(--royal-deep)]">
                  <MapPin size={20} />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-[var(--royal-deep)]">{h}</h3>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-[var(--gold)] font-semibold">Service Hub</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full gradient-royal px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-[var(--royal)]/30 hover:-translate-y-0.5 transition-transform">
              Request a custom quote <ArrowRight size={14} />
            </Link>
            <a href={wa(BUSINESS.primaryWhatsApp)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-[#25D366]/30 hover:-translate-y-0.5 transition-transform">
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Services;
